import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  Leaf, 
  Users, 
  Package, 
  Recycle,
  TrendingUp,
  TreePine,
  Scale,
  BarChart3,
  Loader2,
  Calendar
} from "lucide-react";

const StatsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats/public`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const categoryNames = {
    muebles: "Muebles",
    electrodomesticos: "Electrodomésticos",
    deporte: "Deporte y Ocio",
    juguetes: "Juguetes",
    hogar: "Artículos del Hogar",
    otros: "Otros"
  };

  const monthNames = {
    "01": "Ene", "02": "Feb", "03": "Mar", "04": "Abr", "05": "May", "06": "Jun",
    "07": "Jul", "08": "Ago", "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dic"
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return "";
    const parts = monthStr.split("-");
    if (parts.length === 2) {
      return `${monthNames[parts[1]] || parts[1]} ${parts[0].slice(2)}`;
    }
    return monthStr;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#374B67]" />
        </div>
      </Layout>
    );
  }

  const maxProductCount = Math.max(...(stats?.monthly_trends?.products?.map(p => p.count) || [1]), 1);
  const maxReservationCount = Math.max(...(stats?.monthly_trends?.reservations?.map(r => r.count) || [1]), 1);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <Leaf className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#374B67] mb-2" data-testid="stats-title">
            Impacto Ambiental
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Juntos estamos contribuyendo a la economía circular en Majadahonda. 
            Estos son nuestros logros colectivos.
          </p>
        </div>

        {/* Main KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-[#374B67] to-[#2C3E56] text-white">
            <CardContent className="p-6 text-center">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-80" />
              <p className="text-4xl font-bold">{stats?.summary?.total_users || 0}</p>
              <p className="text-sm opacity-80">Vecinos registrados</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-[#69C7EA] to-[#4BA8CC] text-white">
            <CardContent className="p-6 text-center">
              <Package className="w-10 h-10 mx-auto mb-3 opacity-80" />
              <p className="text-4xl font-bold">{stats?.summary?.products_donated || 0}</p>
              <p className="text-sm opacity-80">Productos donados</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-6 text-center">
              <Recycle className="w-10 h-10 mx-auto mb-3 opacity-80" />
              <p className="text-4xl font-bold">{stats?.summary?.products_delivered || 0}</p>
              <p className="text-sm opacity-80">Objetos reutilizados</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-6 text-center">
              <TrendingUp className="w-10 h-10 mx-auto mb-3 opacity-80" />
              <p className="text-4xl font-bold">{stats?.summary?.active_listings || 0}</p>
              <p className="text-sm opacity-80">Disponibles ahora</p>
            </CardContent>
          </Card>
        </div>

        {/* Environmental Impact */}
        <Card className="border-0 shadow-xl mb-12 overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-green-500 p-6 text-white">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Leaf className="w-7 h-7" />
              Impacto Medioambiental Estimado
            </h2>
            <p className="opacity-80 mt-1">Contribución a la sostenibilidad de Majadahonda</p>
          </div>
          <CardContent className="p-0">
            <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-200">
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Scale className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-4xl font-bold text-green-600">
                  {stats?.environmental_impact?.kg_waste_diverted || 0} kg
                </p>
                <p className="text-slate-600 mt-2">Residuos evitados</p>
                <p className="text-xs text-slate-400 mt-1">Objetos que no van al vertedero</p>
              </div>

              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="text-blue-600 font-bold text-lg">CO₂</div>
                </div>
                <p className="text-4xl font-bold text-blue-600">
                  {stats?.environmental_impact?.co2_saved_kg || 0} kg
                </p>
                <p className="text-slate-600 mt-2">CO₂ ahorrado</p>
                <p className="text-xs text-slate-400 mt-1">Emisiones evitadas</p>
              </div>

              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TreePine className="w-8 h-8 text-emerald-600" />
                </div>
                <p className="text-4xl font-bold text-emerald-600">
                  {stats?.environmental_impact?.trees_equivalent || 0}
                </p>
                <p className="text-slate-600 mt-2">Árboles equivalentes</p>
                <p className="text-xs text-slate-400 mt-1">Absorción anual de CO₂</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Trends */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Products Trend */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#374B67]">
                <Calendar className="w-5 h-5" />
                Productos Donados (Últimos 6 meses)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.monthly_trends?.products?.length > 0 ? (
                <div className="space-y-3">
                  {stats.monthly_trends.products.map((item) => (
                    <div key={item.month} className="flex items-center gap-3">
                      <span className="text-sm text-slate-500 w-16">{formatMonth(item.month)}</span>
                      <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-[#374B67] to-[#69C7EA] rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                          style={{ width: `${Math.max((item.count / maxProductCount) * 100, 10)}%` }}
                        >
                          <span className="text-xs text-white font-semibold">{item.count}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                  <p>Aún no hay datos de tendencia</p>
                  <p className="text-sm">Los datos aparecerán cuando se donen productos</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Reservations Trend */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#374B67]">
                <TrendingUp className="w-5 h-5" />
                Reservas (Últimos 6 meses)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.monthly_trends?.reservations?.length > 0 ? (
                <div className="space-y-3">
                  {stats.monthly_trends.reservations.map((item) => (
                    <div key={item.month} className="flex items-center gap-3">
                      <span className="text-sm text-slate-500 w-16">{formatMonth(item.month)}</span>
                      <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                          style={{ width: `${Math.max((item.count / maxReservationCount) * 100, 10)}%` }}
                        >
                          <span className="text-xs text-white font-semibold">{item.count}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <TrendingUp className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                  <p>Aún no hay datos de reservas</p>
                  <p className="text-sm">Los datos aparecerán con las primeras reservas</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Products by Category */}
        <div className="grid md:grid-cols-2 gap-8">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#374B67]">
                <BarChart3 className="w-5 h-5" />
                Productos por Categoría
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.by_category?.length > 0 ? (
                <div className="space-y-4">
                  {stats.by_category.map((cat, index) => {
                    const maxCount = stats.by_category[0]?.count || 1;
                    const percentage = (cat.count / maxCount) * 100;
                    return (
                      <div key={cat.category}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-600">{categoryNames[cat.category] || cat.category}</span>
                          <span className="font-semibold text-[#374B67]">{cat.count}</span>
                        </div>
                        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-[#374B67] to-[#69C7EA] rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">
                  Aún no hay datos de categorías
                </p>
              )}
            </CardContent>
          </Card>

          {/* How You Can Help */}
          <Card className="border-0 shadow-lg bg-gradient-to-br from-slate-50 to-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#374B67]">
                <Recycle className="w-5 h-5" />
                ¿Cómo puedes contribuir?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-white rounded-xl">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-green-600">1</span>
                </div>
                <div>
                  <h4 className="font-semibold text-[#374B67]">Dona objetos</h4>
                  <p className="text-sm text-slate-600">Gana 100 puntos por cada producto que dones</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 p-4 bg-white rounded-xl">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-blue-600">2</span>
                </div>
                <div>
                  <h4 className="font-semibold text-[#374B67]">Reutiliza</h4>
                  <p className="text-sm text-slate-600">Da una segunda vida a objetos del catálogo</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 p-4 bg-white rounded-xl">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-amber-600">3</span>
                </div>
                <div>
                  <h4 className="font-semibold text-[#374B67]">Comparte</h4>
                  <p className="text-sm text-slate-600">Invita a tus vecinos a unirse a la comunidad</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer Message */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-4 bg-green-50 border border-green-200 rounded-2xl">
            <Leaf className="w-6 h-6 text-green-600" />
            <p className="text-green-700">
              <span className="font-semibold">Cada objeto reutilizado cuenta.</span>
              {" "}Juntos construimos un Majadahonda más sostenible.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default StatsPage;
